import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const WorkExperience = ({ onNext, onPrevious, onSave, savedData = {} }) => {
  const [experiences, setExperiences] = useState(savedData?.experiences || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentExperience, setCurrentExperience] = useState({
    jobTitle: '',
    company: '',
    location: '',
    startDate: '',
    endDate: '',
    isCurrentRole: false,
    description: '',
    achievements: ['']
  });

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'executive', label: 'Executive Level (10+ years)' }
  ];

  const [overallExperience, setOverallExperience] = useState(savedData?.overallExperience || '');

  const addExperience = () => {
    if (currentExperience?.jobTitle && currentExperience?.company) {
      const newExperience = {
        ...currentExperience,
        id: Date.now(),
        achievements: currentExperience?.achievements?.filter(achievement => achievement?.trim())
      };
      setExperiences([...experiences, newExperience]);
      setCurrentExperience({
        jobTitle: '',
        company: '',
        location: '',
        startDate: '',
        endDate: '',
        isCurrentRole: false,
        description: '',
        achievements: ['']
      });
      setShowAddForm(false);
    }
  };

  const removeExperience = (id) => {
    setExperiences(experiences?.filter(exp => exp?.id !== id));
  };

  const addAchievement = () => {
    setCurrentExperience({
      ...currentExperience,
      achievements: [...currentExperience?.achievements, '']
    });
  };

  const updateAchievement = (index, value) => {
    const updatedAchievements = [...currentExperience?.achievements];
    updatedAchievements[index] = value;
    setCurrentExperience({
      ...currentExperience,
      achievements: updatedAchievements
    });
  };

  const removeAchievement = (index) => {
    if (currentExperience?.achievements?.length > 1) {
      const updatedAchievements = currentExperience?.achievements?.filter((_, i) => i !== index);
      setCurrentExperience({
        ...currentExperience,
        achievements: updatedAchievements
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const calculateDuration = (startDate, endDate, isCurrent) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = isCurrent ? new Date() : new Date(endDate);
    const months = (end?.getFullYear() - start?.getFullYear()) * 12 + (end?.getMonth() - start?.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    if (remainingMonths === 0) return `${years} year${years !== 1 ? 's' : ''}`;
    return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  };

  const handleNext = () => {
    const data = { 
      experiences, 
      overallExperience 
    };
    onSave(data);
    onNext();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-text-primary mb-2">Work Experience</h3>
        <p className="text-text-secondary">
          Share your professional journey to help us understand your background and career progression.
        </p>
      </div>
      {/* Overall Experience Level */}
      <div className="mb-6">
        <Select
          label="Overall Experience Level"
          description="Select your current professional level"
          options={experienceLevels}
          value={overallExperience}
          onChange={setOverallExperience}
          placeholder="Choose your experience level"
          required
        />
      </div>
      {/* Experience List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-text-primary">
            Work History ({experiences?.length})
          </h4>
          <Button
            variant="outline"
            onClick={() => setShowAddForm(true)}
            iconName="Plus"
            size="sm"
          >
            Add Experience
          </Button>
        </div>

        {experiences?.length === 0 ? (
          <div className="text-center py-8 text-text-secondary">
            <Icon name="Briefcase" size={48} className="mx-auto mb-4 opacity-50" />
            <p>No work experience added yet.</p>
            <p className="text-sm">Add your professional experience to get better job matches.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {experiences?.map((exp) => (
              <div key={exp?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="font-semibold text-text-primary">{exp?.jobTitle}</h5>
                    <p className="text-text-secondary">{exp?.company}</p>
                    {exp?.location && (
                      <p className="text-sm text-text-secondary flex items-center mt-1">
                        <Icon name="MapPin" size={14} className="mr-1" />
                        {exp?.location}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeExperience(exp?.id)}
                    className="text-destructive hover:text-destructive/80 spring-transition"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>

                <div className="flex items-center space-x-4 mb-3 text-sm text-text-secondary">
                  <span>{formatDate(exp?.startDate)} - {exp?.isCurrentRole ? 'Present' : formatDate(exp?.endDate)}</span>
                  <span>•</span>
                  <span>{calculateDuration(exp?.startDate, exp?.endDate, exp?.isCurrentRole)}</span>
                  {exp?.isCurrentRole && (
                    <>
                      <span>•</span>
                      <span className="text-success font-medium">Current Role</span>
                    </>
                  )}
                </div>

                {exp?.description && (
                  <p className="text-sm text-text-secondary mb-3">{exp?.description}</p>
                )}

                {exp?.achievements?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-text-primary mb-2">Key Achievements:</p>
                    <ul className="space-y-1">
                      {exp?.achievements?.map((achievement, index) => (
                        <li key={index} className="text-sm text-text-secondary flex items-start">
                          <Icon name="ChevronRight" size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add Experience Form */}
      {showAddForm && (
        <div className="mb-6 p-6 border border-border rounded-lg bg-muted">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-text-primary">Add Work Experience</h4>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowAddForm(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Job Title"
              type="text"
              placeholder="e.g., Software Engineer"
              value={currentExperience?.jobTitle}
              onChange={(e) => setCurrentExperience({...currentExperience, jobTitle: e?.target?.value})}
              required
            />
            <Input
              label="Company"
              type="text"
              placeholder="e.g., Tech Corp Inc."
              value={currentExperience?.company}
              onChange={(e) => setCurrentExperience({...currentExperience, company: e?.target?.value})}
              required
            />
            <Input
              label="Location"
              type="text"
              placeholder="e.g., San Francisco, CA"
              value={currentExperience?.location}
              onChange={(e) => setCurrentExperience({...currentExperience, location: e?.target?.value})}
            />
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="currentRole"
                checked={currentExperience?.isCurrentRole}
                onChange={(e) => setCurrentExperience({...currentExperience, isCurrentRole: e?.target?.checked})}
                className="rounded border-border"
              />
              <label htmlFor="currentRole" className="text-sm text-text-primary">
                This is my current role
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Start Date"
              type="month"
              value={currentExperience?.startDate}
              onChange={(e) => setCurrentExperience({...currentExperience, startDate: e?.target?.value})}
              required
            />
            {!currentExperience?.isCurrentRole && (
              <Input
                label="End Date"
                type="month"
                value={currentExperience?.endDate}
                onChange={(e) => setCurrentExperience({...currentExperience, endDate: e?.target?.value})}
                required
              />
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Job Description
            </label>
            <textarea
              placeholder="Describe your role and responsibilities..."
              value={currentExperience?.description}
              onChange={(e) => setCurrentExperience({...currentExperience, description: e?.target?.value})}
              className="w-full p-3 border border-border rounded-md resize-none h-24"
            />
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-text-primary">
                Key Achievements
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={addAchievement}
                iconName="Plus"
              >
                Add Achievement
              </Button>
            </div>
            <div className="space-y-2">
              {currentExperience?.achievements?.map((achievement, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    type="text"
                    placeholder="e.g., Increased team productivity by 25%"
                    value={achievement}
                    onChange={(e) => updateAchievement(index, e?.target?.value)}
                    className="flex-1"
                  />
                  {currentExperience?.achievements?.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAchievement(index)}
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button onClick={addExperience}>
              Add Experience
            </Button>
          </div>
        </div>
      )}
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={!overallExperience}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Preferences
        </Button>
      </div>
    </div>
  );
};

export default WorkExperience;